import { cityNameValidation, stateNameValidation, zipCodeValidation } from "../apiCaching/POST-Requests/helperModules/handleBookingSubmit";
import { REGEX } from "../constants/regex";
import { startLoader, stopLoader } from "../util/loader"
import { NearbyLocations } from "../util/nearbyLocations";
import { getFranchiseDetails } from "../util/share";

const PHONE_ICON_SVG_PATH = "/_assets-global/images/hero-icon-sprite.svg#phone-outline-primary";

interface FormFields {
  address1: string;
  address2: string;
  city: string;
  state: string;
  zipCode: string;
}

interface StepElements {
  [key: string]: HTMLElement | null;
}

export const formatPhoneNumber = (phoneNumber: string): string => {
  if (!phoneNumber) return '';
  return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6)}`;
};    

export const getPhoneLink = (phoneNumber: string): string => {
  return `tel:${phoneNumber?.replace(/\D/g, '')}`;
};

export const createPhoneElement = (phoneNumber: string): string => {
  const formattedPhone = formatPhoneNumber(phoneNumber);
  return `
    <svg aria-hidden="true" class="h-6 w-6">
      <use xlink:href="${PHONE_ICON_SVG_PATH}"></use>
    </svg>
    ${formattedPhone}
  `;
};
export class FindUsAddressBumper {
  private readonly sendButton: HTMLButtonElement | null;
  private readonly formElement: HTMLFormElement;
  private readonly steps: StepElements;
  private zipCodeValue: string = "";
  private readonly handleClose: () => void;

  private readonly STEP_SELECTORS = {
    step1: ".dynamic-step-1",
    step2: ".dynamic-step-2",
    step3: ".dynamic-step-3",
    step4: ".dynamic-step-4",
    step5: ".dynamic-step-5"
  };

  private readonly FORM_SELECTORS = {
    form: '.dynamic-step-2 .modal-search',
    sendButton: '.dynamic-step-2 .form-cta button',
    closeButton: '#dynamic-close-local-modal',
    errorCloseButton: '#dynamic-close-btn-error',
    inputAddress1: '.dynamic-step-2 #dynamic-modal-address-1',
    inputAddress2: '.dynamic-step-2 #dynamic-modal-address-2',
    inputCity: '.dynamic-step-2 #dynamic-modal-city',
    inputState: '.dynamic-step-2 #dynamic-modal-state',
    inputZipCode: '.dynamic-step-2 #dynamic-modal-zipcode'
  };

  constructor() {
    this.steps = this.initializeSteps();
    this.formElement = document.querySelector(this.FORM_SELECTORS.form) as HTMLFormElement;
    this.sendButton = document.querySelector(this.FORM_SELECTORS.sendButton) as HTMLButtonElement;
    this.handleClose = this.handleModalClose.bind(this);
    
    this.initializeEventListeners();
    this.setupInlineValidation();
  }

  private initializeSteps(): StepElements {
    return Object.entries(this.STEP_SELECTORS).reduce((acc, [key, selector]) => {
      acc[key] = document.querySelector(selector);
      return acc;
    }, {} as StepElements);
  }

  private initializeEventListeners(): void {
    if (this.sendButton) {
      this.sendButton.addEventListener("click", () => this.handleNextBtnClick());
    }

    const closeButton = document.querySelector(this.FORM_SELECTORS.closeButton);
    if (closeButton) {
      closeButton.addEventListener('click', this.handleClose);
    }

    const dynamicCloseButton = document.querySelector(this.FORM_SELECTORS.errorCloseButton);
    if (dynamicCloseButton) {
      dynamicCloseButton.addEventListener('click', () => this.resetModalClose());
    }
  }


  private resetModalClose(): void {
    this.formElement.reset();
    this.toggleStep('step4', false);
    this.toggleStep('step1', true);
  }
  private setupInlineValidation(): void {
    const fields = {
      address1: document.querySelector(this.FORM_SELECTORS.inputAddress1) as HTMLInputElement,
      address2: document.querySelector(this.FORM_SELECTORS.inputAddress2) as HTMLInputElement,
      city: document.querySelector(this.FORM_SELECTORS.inputCity) as HTMLInputElement,
      state: document.querySelector(this.FORM_SELECTORS.inputState) as HTMLSelectElement,
      zipCode: document.querySelector(this.FORM_SELECTORS.inputZipCode) as HTMLInputElement
    };

    const validationHandlers = {
      address1: () => this.addressValidation(fields.address1.value, true),
      address2: () => this.addressValidation(fields.address2.value, false),
      city: () => this.cityValidation(fields.city.value),
      state: () => this.stateValidation(fields.state.value),
      zipCode: () => this.zipValidation(fields.zipCode.value)
    };

    Object.entries(fields).forEach(([fieldName, element]) => {
      if (!element) return;
      
      const eventType = fieldName === 'state' ? 'change' : 'input';
      element.addEventListener(eventType, (e) => {
        const target = e.target as HTMLInputElement | HTMLSelectElement;
        this.validateField(target, validationHandlers[fieldName as keyof typeof validationHandlers]);
      });
    });
  }

  private validateField(element: HTMLInputElement | HTMLSelectElement, validationFunction: () => string): void {
    const error = validationFunction();
    const errorElement = element.nextElementSibling as HTMLSpanElement;
    if (error) {
      element.classList.add('invalid-field');
      if (errorElement?.classList.contains('error-msg')) {
        errorElement.textContent = error;
      } else {
        const newErrorElement = document.createElement('span');
        newErrorElement.className = 'error-msg';
        newErrorElement.textContent = error;
        element.parentNode?.insertBefore(newErrorElement, element.nextSibling);
      }
    } else {
      element.classList.remove('invalid-field');
      if (errorElement?.classList.contains('error-msg')) {
        errorElement.remove();
      }
    }
  }

  private getFormFields(): FormFields {
    const getValue = (selector: string): string => 
      (document.querySelector(selector) as HTMLInputElement | HTMLSelectElement)?.value || '';

    return {
      zipCode: getValue(this.FORM_SELECTORS.inputZipCode),
      address1: getValue(this.FORM_SELECTORS.inputAddress1),
      address2: getValue(this.FORM_SELECTORS.inputAddress2),
      city: getValue(this.FORM_SELECTORS.inputCity),
      state: getValue(this.FORM_SELECTORS.inputState)
    };
  }

  private addressValidation(inputVal: string, isRequired: boolean): string {
    const trimmedValue = inputVal.trim();
    
    if (isRequired && !trimmedValue) {
      return 'Address Line 1 is required.';
    }
    
    if (trimmedValue && !REGEX.noBracketAddress.test(trimmedValue)) {
      return 'Enter valid address.';
    }
    
    return '';
  }

  private zipValidation(inputVal: string): string {
     return zipCodeValidation(inputVal);
  }

  private cityValidation(inputVal: string): string {
    return cityNameValidation(inputVal);
  }

  private stateValidation(inputVal: string): string {
    return stateNameValidation(inputVal);
  }


  public validateForm(): boolean {
    const formFields = this.getFormFields();
    const fieldValidations = [
      { id: 'dynamic-modal-address-1', error: this.addressValidation(formFields.address1, true) },
      { id: 'dynamic-modal-address-2', error: this.addressValidation(formFields.address2, false) },
      { id: 'dynamic-modal-zipcode', error: this.zipValidation(formFields.zipCode) },
      { id: 'dynamic-modal-city', error: this.cityValidation(formFields.city) },
      { id: 'dynamic-modal-state', error: this.stateValidation(formFields.state) }
    ];

    let isValid = true;

    fieldValidations.forEach(({ id, error }) => {
      const field = document.getElementById(id);
      if (!field || !error) return;
      
      this.showFieldError(field, error);
      isValid = false;
    });

    return isValid;
  }

  private showFieldError(field: HTMLElement, errorMessage: string): void {
    if (!field.parentNode) return;

    let errorElement = field.parentNode.querySelector('.error-msg');
    
    if (!errorElement) {
      errorElement = document.createElement('span');
      errorElement.className = 'error-msg';
      field.parentNode.insertBefore(errorElement, field.nextSibling);
    }
    
    errorElement.textContent = errorMessage;
    errorElement.classList.remove('hidden');
    field.classList.add('invalid-field');
  }

  public handleNextBtnClick(): void {
    if (!this.validateForm()) return;
    
    startLoader();
    this.handleNextBtnSubmit();
  }

  public handleNextBtnSubmit(): void {
    const formFields = this.getFormFields();
    const { address1, address2, city, state, zipCode } = formFields;
    const addressParam = [address1, address2, city, state, zipCode].join(' ');
    
    this.zipCodeValue = zipCode;
    
    getFranchiseDetails(addressParam)
      .then((result) => {
        if (!result.length) {
          this.handleNoResults();
          return;
        }
        
        this.showStep('step5');
        this.updateLocationDetails(result[0], '.dynamic-step-5', result[0].locationWebsiteUrl);
        
        const zipCodeSpan = document.querySelector(".dynamic-step-5 .happy-text span") as HTMLElement;
        if (zipCodeSpan && zipCode) {
          stopLoader();
          zipCodeSpan.textContent = zipCode;
        }
      })
      .catch(error => {
        console.error('Error fetching franchise details:', error);
        this.handleNoResults();
      });
  }
  private handleNoResults(): void {
    const zipCode = this.getFormFields().zipCode;
    if (zipCode) {
      this.handleNearbyLocationsFlow(zipCode);
      stopLoader();
    }
  }

  private handleNearbyLocationsFlow(zipCode: string): void {
    const units = "50miles";
    const franchiseLimit = 50;
    this.callNearbyLocations(units, franchiseLimit, zipCode);
  }
  private handleErrorScenario(zipCode: string): void {
    this.toggleStep('step2', false);
    this.toggleStep('step4', true);
    
    const errorMessage = this.steps.step4?.querySelector('.modal-text') as HTMLElement;
    if (errorMessage) {
      errorMessage.textContent = `We're sorry. We don't currently provide service to ${this.zipCodeValue}.`;
    }
  }
  private callNearbyLocations(units: string, franchiseLimit: number, zipCode: string): void {
    new NearbyLocations(units, franchiseLimit)
      .searchNearbyLocations(zipCode)
      .then((data) => this.displayNearbyLocations(data))
      .catch(() => this.handleErrorScenario(zipCode));
  }

private updateLocationDetails(location: any, stepSelector: string, websiteUrl: string): void {
  if (!location) return;

  const stepElement = document.querySelector(stepSelector);
  if (!stepElement) return;

  // Update heading
  const heading = stepElement.querySelector('.heading-container h3') as HTMLElement;
  if (heading) heading.textContent = location.doingBusinessAs;

  // Update phone
  const phoneWrapper = stepElement.querySelector('.phone-num-wrapper') as HTMLAnchorElement;
  if (phoneWrapper) {
    phoneWrapper.href = getPhoneLink(location.phoneNumber);
    phoneWrapper.innerHTML = createPhoneElement(location.phoneNumber);
  }

  // Update website
  const websiteLink = stepElement.querySelector('.view-web-link') as HTMLAnchorElement;
  if (websiteLink) {
    websiteLink.href = websiteUrl;
  }
}

  private displayNearbyLocations(data: any): void {
    if (!data?.length) return this.handleErrorScenario(data);
    
    this.showStep('step5');
    const firstLocation = data[0];
    if (!firstLocation) return;

    this.updateNotFoundUI();
    this.updateLocationDetails(firstLocation, '.dynamic-step-5', firstLocation.websiteUrl);
  }

  private updateNotFoundUI(): void {
    const step5 = this.steps.step5;
    if (!step5) return;

    const notFoundWrapper = step5.querySelector(".loc-not-found-wrapper");
    notFoundWrapper?.classList.remove("hidden");
    
    const notFoundZipSpan = step5.querySelector(".loc-not-found-wrapper p span");
    if (notFoundZipSpan && this.zipCodeValue) {
      notFoundZipSpan.textContent = this.zipCodeValue;
    }
    
    step5.querySelector(".not-found-text")?.classList.remove("hidden");
    step5.querySelector(".happy-text")?.classList.add("hidden");
  }
  private clearValidationErrors(): void {
    const step2 = this.steps.step2;
    if (!step2) return;
    
    step2.querySelectorAll('.error-msg').forEach(e => e.remove());
    step2.querySelectorAll('.invalid-field').forEach(e => e.classList.remove('invalid-field'));
  }

  private handleModalClose(): void {
    this.formElement.reset();
    this.clearValidationErrors();
    const closeButton = document.querySelector(this.FORM_SELECTORS.closeButton);
    if (closeButton) {
      closeButton.removeEventListener('click', this.handleClose);
    }
  }

  private showStep(stepName: string, show: boolean = true): void {
    const step = this.steps[stepName];
    if (!step) return;
    
    if (show) {
      // Hide all steps first
      Object.values(this.steps).forEach(s => s?.classList.add('hidden'));
      // Then show the requested step
      step.classList.remove('hidden');
    } else {
      step.classList.add('hidden');
    }
  }

  private toggleStep(stepName: string, show: boolean): void {
    const step = this.steps[stepName];
    if (!step) return;
    
    if (show) {
      step.classList.remove('hidden');
    } else {
      step.classList.add('hidden');
    }
  }

}